"use client";

import { useLazyQuery } from "@apollo/client";
import { Loader2, Search, X } from "lucide-react";
import UserAvatar from "components/ui/v2/user-avatar-compat-v2";
import { Button as V2Button } from "components/ui/v2/button";
import { gqlUser } from "gql";
import { IVendorUserListResponse } from "gql/User/queries";
import useTranslation from "next-translate/useTranslation";
import * as React from "react";
import { createPortal } from "react-dom";
import { IUser } from "types/User.types";
import useDebounce from "utils/hooks/useDebounce";
import { getAppNavbarHeight } from "hooks/useAppNavbar";
import { cn } from "@/lib/utils";
import { SearchInputAuroraGlow } from "./composites/search-input-aurora-glow";

export interface NavbarUserSearchProps {
  onSelectUser: (user: IUser) => void;
  placeholder?: string;
  autoFocus?: boolean;
  onFocusChange?: (focused: boolean) => void;
  onSearchActiveChange?: (active: boolean) => void;
  trigger?: "inline" | "icon";
}

const RECENT_USERS_STORAGE_KEY = "recentNavbarSearchUsers";
const MORPH_MS = 480;
const BACKDROP_FADE_MS = 420;

type RectBox = { top: number; left: number; width: number; height: number };

const getExpandedBox = (): RectBox => {
  const header = getAppNavbarHeight();
  const width = Math.min(640, window.innerWidth - 32);
  const height = 52;
  return {
    top: header + 14,
    left: (window.innerWidth - width) / 2,
    width,
    height
  };
};

const rectFromDom = (rect: DOMRect): RectBox => ({
  top: rect.top,
  left: rect.left,
  width: rect.width,
  height: rect.height
});

const getUserLabel = (user: Partial<IUser>) =>
  `${user.name || ""} ${user.lastname || ""}`.trim();

const getUserMeta = (user: Partial<IUser>) => {
  if (typeof user.role === "object" && user.role?.name) return user.role.name;
  if (typeof user.role === "string" && user.role) return user.role;
  return user.email || "";
};

export function NavbarUserSearch({
  onSelectUser,
  placeholder,
  autoFocus = false,
  onFocusChange,
  onSearchActiveChange,
  trigger = "inline"
}: NavbarUserSearchProps) {
  const { t } = useTranslation("common");
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const [mounted, setMounted] = React.useState(false);
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [isBackdropVisible, setIsBackdropVisible] = React.useState(false);
  const [isMorphDone, setIsMorphDone] = React.useState(false);
  const [fromBox, setFromBox] = React.useState<RectBox | null>(null);
  const [toBox, setToBox] = React.useState<RectBox | null>(null);
  const [query, setQuery] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [, setRecentUsers] = React.useState<IUser[]>([]);

  const debouncedQuery = useDebounce(query, 1000);
  const trimmedQuery = query.trim();
  const hasSearchTerm = trimmedQuery.length > 1;
  const placeholderText = placeholder || t("Buscar usuarios");
  const showResults = isMorphDone && hasSearchTerm;

  const [getActiveUsers, { data: activeUsersData, error: apolloError }] =
    useLazyQuery<IVendorUserListResponse>(gqlUser.queries.GET_VENDOR_USER_LIST, {
      fetchPolicy: "network-only",
      errorPolicy: "all"
    });

  const searchResults = React.useMemo(
    () => activeUsersData?.vendorUserList || [],
    [activeUsersData?.vendorUserList]
  );

  const openExpand = React.useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;

    setFromBox(rectFromDom(rect));
    setToBox(getExpandedBox());
    setIsExpanded(true);
    setIsBackdropVisible(false);
    setIsMorphDone(false);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setIsMorphDone(true);
        setIsBackdropVisible(true);
      });
    });
  }, []);

  const closeExpand = React.useCallback(() => {
    setIsBackdropVisible(false);
    setIsMorphDone(false);
    window.setTimeout(() => {
      setIsExpanded(false);
      setFromBox(null);
      setToBox(null);
    }, Math.max(MORPH_MS, BACKDROP_FADE_MS));
  }, []);

  React.useEffect(() => setMounted(true), []);

  React.useEffect(() => {
    if (!isExpanded || !isMorphDone) return;
    const id = window.setTimeout(() => inputRef.current?.focus(), 60);
    return () => window.clearTimeout(id);
  }, [isExpanded, isMorphDone, autoFocus]);

  React.useEffect(() => {
    if (!isExpanded) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isExpanded]);

  React.useEffect(() => {
    if (!isExpanded) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeExpand();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isExpanded, closeExpand]);

  React.useEffect(() => {
    const stored = localStorage.getItem(RECENT_USERS_STORAGE_KEY);
    if (!stored) return;
    try {
      setRecentUsers(JSON.parse(stored).slice(0, 5));
    } catch {
      /* ignore */
    }
  }, []);

  React.useEffect(() => {
    if (!hasSearchTerm || debouncedQuery.trim().length <= 1) return;
    getActiveUsers({
      variables: {
        vendorUserListInput: { searchText: debouncedQuery.trim() }
      }
    }).catch(() => {
      setError(t("Error searching users"));
      setLoading(false);
    });
  }, [debouncedQuery, getActiveUsers, hasSearchTerm, t]);

  React.useEffect(() => {
    if (!apolloError) return;
    setError(t("Error searching users"));
    setLoading(false);
  }, [apolloError, t]);

  React.useEffect(() => {
    if (hasSearchTerm) setLoading(true);
    else {
      setLoading(false);
      setError(null);
    }
  }, [hasSearchTerm]);

  React.useEffect(() => {
    if (activeUsersData?.vendorUserList) {
      setLoading(false);
      setError(null);
    }
  }, [activeUsersData?.vendorUserList]);

  React.useEffect(() => {
    onFocusChange?.(isExpanded);
  }, [isExpanded, onFocusChange]);

  React.useEffect(() => {
    onSearchActiveChange?.(isExpanded);
  }, [isExpanded, onSearchActiveChange]);

  const saveToRecentUsers = React.useCallback((user: IUser) => {
    setRecentUsers((prev) => {
      const updated = [user, ...prev.filter((u) => u.id !== user.id)].slice(0, 5);
      localStorage.setItem(RECENT_USERS_STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const handleSelectUser = React.useCallback(
    (user: IUser) => {
      saveToRecentUsers(user);
      onSelectUser(user);
      setQuery("");
      setError(null);
      closeExpand();
    },
    [onSelectUser, saveToRecentUsers, closeExpand]
  );

  const box = isMorphDone && toBox ? toBox : fromBox;
  const resultsTop = toBox ? toBox.top + toBox.height + 12 : 0;

  const overlay =
    mounted && isExpanded && fromBox && toBox && box
      ? createPortal(
          <div
            className="search-expand-root"
            role="dialog"
            aria-modal="true"
            aria-label={placeholderText}
          >
            <button
              type="button"
              className={cn(
                "search-expand-backdrop",
                isBackdropVisible && "search-expand-backdrop--visible"
              )}
              aria-label={t("Close")}
              onClick={closeExpand}
            />

            <div
              className={cn(
                "search-expand-pill",
                isMorphDone
                  ? "search-expand-pill--expanded"
                  : "search-expand-pill--morphing"
              )}
              style={{
                top: isMorphDone ? toBox.top : fromBox.top,
                left: isMorphDone ? toBox.left : fromBox.left,
                width: isMorphDone ? toBox.width : fromBox.width,
                height: isMorphDone ? toBox.height : fromBox.height
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="search-expand-pill-frame">
                {isMorphDone && <SearchInputAuroraGlow />}
                <div className="search-expand-pill-inner search-bar-frosted">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-violet-600 dark:text-violet-300" />
                  <input
                    ref={inputRef}
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      setError(null);
                    }}
                    placeholder={placeholderText}
                    autoComplete="off"
                    aria-label={t("Search users in navbar")}
                    className="min-h-0 min-w-0 flex-1 border-0 bg-transparent px-2 text-center text-xs font-medium text-slate-800 outline-none placeholder:text-center placeholder:text-slate-400 dark:text-zinc-100 dark:placeholder:text-zinc-500"
                  />
                  <button
                    type="button"
                    className={cn(
                      "search-expand-close",
                      isMorphDone && "search-expand-close--visible"
                    )}
                    aria-label={t("Close")}
                    tabIndex={isMorphDone ? 0 : -1}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      closeExpand();
                    }}
                  >
                    <X className="h-4 w-4 shrink-0" />
                  </button>
                </div>
              </div>
            </div>

            {showResults && toBox && (
              <div
                className="search-expand-results motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-top-2 motion-safe:duration-300"
                style={{
                  top: resultsTop,
                  left: toBox.left,
                  width: toBox.width
                }}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-xl dark:border-zinc-800 dark:bg-zinc-950">
                  {loading ? (
                    <div
                      className="flex items-center justify-center gap-2 px-4 py-8 text-slate-500 dark:text-zinc-400"
                      role="status"
                    >
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-sm">{t("Searching")}...</span>
                    </div>
                  ) : error ? (
                    <p className="px-4 py-8 text-center text-sm text-slate-500">
                      {error}
                    </p>
                  ) : searchResults.length === 0 ? (
                    <p className="px-4 py-8 text-center text-sm text-slate-500 dark:text-zinc-400">
                      {t("No users found")}
                    </p>
                  ) : (
                    <ul className="max-h-[min(50vh,360px)] overflow-y-auto p-1.5" role="listbox">
                      {searchResults.map((user) => (
                        <li key={user.id}>
                          <button
                            type="button"
                            role="option"
                            aria-selected={false}
                            className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left hover:bg-slate-50 dark:hover:bg-zinc-900"
                            onClick={() => handleSelectUser(user)}
                          >
                            <UserAvatar user={user} showName={false} size={36} />
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-semibold text-slate-800 dark:text-zinc-100">
                                {getUserLabel(user)}
                              </p>
                              <p className="truncate text-xs text-slate-500 dark:text-zinc-400">
                                {getUserMeta(user)}
                              </p>
                            </div>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </div>,
          document.body
        )
      : null;

  const triggerClass =
    trigger === "icon"
      ? undefined
      : cn(
          "search-bar-frosted relative z-[1] flex h-8 w-full items-center justify-center rounded-full",
          // Sin `transition-opacity` — el trigger debe aparecer/desaparecer
          // **instantáneamente** para que el swap visual con la pill portada sea
          // imperceptible. Cualquier transición aquí produce un parpadeo al
          // cerrar: el portal se desmonta cuando termina el morph-back, pero el
          // trigger todavía está fade-in-eando → vacío visual ~200ms. Como la
          // pill a tamaño del trigger (border-radius 9999px clampeado a 16px)
          // se ve idéntica al trigger `rounded-full`, el swap directo es
          // visualmente continuo.
          "hover:border-violet-300/50",
          isExpanded && "pointer-events-none opacity-0"
        );

  const triggerInner =
    trigger === "icon" ? (
      <Search className="h-4 w-4" />
    ) : (
      <>
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400 dark:text-zinc-500" />
        <span
          className={cn(
            "w-full truncate px-8 text-center text-xs font-medium",
            query ? "text-slate-700 dark:text-zinc-100" : "text-slate-400 dark:text-zinc-500"
          )}
        >
          {query || placeholderText}
        </span>
      </>
    );

  const triggerShell = (
    <>
      <SearchInputAuroraGlow size="compact" showOnHover />
      {trigger === "icon" ? (
        <div
          className={cn("relative z-[1]", isExpanded && "pointer-events-none opacity-0")}
        >
          <V2Button
            ref={triggerRef as React.Ref<HTMLButtonElement>}
            type="button"
            variant="ghost"
            size="icon"
            shape="soft"
            aria-label={placeholderText}
            onClick={openExpand}
          >
            {triggerInner}
          </V2Button>
        </div>
      ) : (
        <button
          ref={triggerRef}
          type="button"
          className={triggerClass}
          aria-label={placeholderText}
          onClick={openExpand}
        >
          {triggerInner}
        </button>
      )}
    </>
  );

  return (
    <>
      <div
        className={cn(
          "search-bar-trigger",
          trigger === "icon" ? "inline-flex" : "relative w-full"
        )}
      >
        <div
          className={cn(
            "search-bar-trigger-shell",
            trigger === "icon" ? "inline-flex rounded-full" : "w-full"
          )}
        >
          {triggerShell}
        </div>
      </div>
      {overlay}
      <style jsx global>{`
        .search-expand-root {
          position: fixed;
          inset: 0;
          z-index: 10050;
          pointer-events: none;
        }
        .search-expand-root > * { pointer-events: auto; }
        .search-expand-backdrop {
          position: fixed;
          inset: 0;
          z-index: 0;
          border: 0;
          padding: 0;
          margin: 0;
          cursor: default;
          background: rgb(255 255 255 / 0.78);
          opacity: 0;
          pointer-events: none;
          transition:
            opacity ${BACKDROP_FADE_MS}ms ease-in-out,
            background-color ${BACKDROP_FADE_MS}ms ease-in-out;
        }
        .search-expand-backdrop--visible {
          opacity: 1;
          pointer-events: auto;
        }
        .dark .search-expand-backdrop {
          background: rgb(0 0 0 / 0.38);
        }
        .search-bar-frosted {
          border: 1px solid rgb(226 232 240 / 0.9);
          background: rgb(255 255 255);
          box-shadow: inset 0 1px 0 rgb(255 255 255 / 0.95), 0 1px 3px rgb(15 23 42 / 0.08);
        }
        .dark .search-bar-frosted {
          border-color: rgb(63 63 70 / 0.9);
          background: rgb(24 24 27);
          box-shadow: inset 0 1px 0 rgb(255 255 255 / 0.08), 0 1px 3px rgb(0 0 0 / 0.28);
        }
        .search-expand-pill {
          position: fixed;
          z-index: 1;
          overflow: hidden;
          border-radius: 9999px;
          box-shadow: 0 1px 2px rgb(15 23 42 / 0.06);
          transition:
            top 0.48s cubic-bezier(0.32,0.72,0,1),
            left 0.48s cubic-bezier(0.32,0.72,0,1),
            width 0.48s cubic-bezier(0.32,0.72,0,1),
            height 0.48s cubic-bezier(0.32,0.72,0,1),
            box-shadow 0.48s cubic-bezier(0.32,0.72,0,1);
          will-change: top, left, width, height;
        }
        .search-expand-pill--morphing { overflow: visible; }
        .search-expand-pill--expanded {
          overflow: visible;
          box-shadow:
            0 0 0 1px rgb(99 102 241 / 0.14),
            0 0 24px -4px rgb(99 102 241 / 0.28),
            0 0 48px -8px rgb(34 211 238 / 0.2),
            0 8px 32px -8px rgb(15 23 42 / 0.2),
            0 20px 56px -16px rgb(167 139 250 / 0.22);
        }
        .search-expand-pill--expanded .search-bar-frosted {
          border-color: rgb(226 232 240 / 0.95);
          background: rgb(255 255 255);
          box-shadow: inset 0 1px 0 rgb(255 255 255 / 0.98), 0 1px 3px rgb(15 23 42 / 0.08);
        }
        .dark .search-expand-pill--expanded .search-bar-frosted {
          border-color: rgb(63 63 70 / 0.95);
          background: rgb(24 24 27);
          box-shadow: inset 0 1px 0 rgb(255 255 255 / 0.08), 0 1px 3px rgb(0 0 0 / 0.32);
        }
        .dark .search-expand-pill--expanded input {
          color: rgb(244 244 245);
        }
        .dark .search-expand-pill--expanded input::placeholder {
          color: rgb(161 161 170);
        }
        .search-expand-pill-frame { position: relative; height: 100%; width: 100%; border-radius: inherit; }
        .search-expand-pill-inner {
          position: relative;
          z-index: 1;
          display: flex;
          height: 100%;
          width: 100%;
          align-items: center;
          border-radius: inherit;
          padding-right: 0.2rem;
          padding-left: 2.5rem;
        }
        .search-expand-results { position: fixed; z-index: 10; }
        .search-expand-close {
          position: relative;
          z-index: 2;
          display: flex;
          height: 2rem;
          width: 2rem;
          flex-shrink: 0;
          align-items: center;
          justify-content: center;
          border: 0;
          border-radius: 9999px;
          background: transparent;
          color: rgb(100 116 139);
          cursor: pointer;
          opacity: 0;
          transform: scale(0.85);
          transition:
            opacity 0.22s ease,
            transform 0.22s cubic-bezier(0.32,0.72,0,1),
            background-color 0.15s ease,
            color 0.15s ease;
          pointer-events: none;
        }
        .search-expand-close--visible { opacity: 1; transform: scale(1); pointer-events: auto; }
        .search-expand-close:hover { background-color: rgb(241 245 249); color: rgb(51 65 85); }
        .dark .search-expand-close { color: rgb(161 161 170); }
        .dark .search-expand-close:hover { background-color: rgb(39 39 42); color: rgb(228 228 231); }
      `}</style>
    </>
  );
}
